import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import koTrans from './locales/ko.json';
import enTrans from './locales/en.json';
import jaTrans from './locales/ja.json';
import esTrans from './locales/es.json';
import ptTrans from './locales/pt.json';
import frTrans from './locales/fr.json';

export const supportedLanguageCodes = ['ko', 'en', 'ja', 'es', 'pt', 'fr'] as const;
export type SupportedLanguageCode = (typeof supportedLanguageCodes)[number];

const supportedLanguageSet = new Set<string>(supportedLanguageCodes);
const languagePreferenceKey = 'mj.preferredLanguage';

const resources = {
  ko: {
    translation: koTrans
  },
  en: {
    translation: enTrans
  },
  ja: {
    translation: jaTrans
  },
  es: {
    translation: esTrans
  },
  pt: {
    translation: ptTrans
  },
  fr: {
    translation: frTrans
  }
};

const normalizeLanguageCode = (value?: string | null): SupportedLanguageCode | null => {
  if (!value) return null;

  const language = value.toLowerCase().split(/[-_]/)[0];
  return supportedLanguageSet.has(language) ? (language as SupportedLanguageCode) : null;
};

const getStoredLanguage = (): SupportedLanguageCode | null => {
  if (typeof window === 'undefined') return null;

  try {
    return normalizeLanguageCode(window.localStorage.getItem(languagePreferenceKey));
  } catch {
    return null;
  }
};

const timeZoneLanguageMap: Record<string, SupportedLanguageCode> = {
  'Asia/Seoul': 'ko',
  'Asia/Tokyo': 'ja',
  'Europe/Madrid': 'es',
  'Atlantic/Canary': 'es',
  'Africa/Ceuta': 'es',
  'America/Mexico_City': 'es',
  'America/Cancun': 'es',
  'America/Bogota': 'es',
  'America/Lima': 'es',
  'America/Santiago': 'es',
  'America/Caracas': 'es',
  'America/Argentina/Buenos_Aires': 'es',
  'America/Montevideo': 'es',
  'America/Asuncion': 'es',
  'America/La_Paz': 'es',
  'America/Guayaquil': 'es',
  'America/Guatemala': 'es',
  'America/El_Salvador': 'es',
  'America/Tegucigalpa': 'es',
  'America/Managua': 'es',
  'America/Costa_Rica': 'es',
  'America/Panama': 'es',
  'America/Santo_Domingo': 'es',
  'America/Puerto_Rico': 'es',
  'Europe/Lisbon': 'pt',
  'Atlantic/Azores': 'pt',
  'Atlantic/Madeira': 'pt',
  'America/Sao_Paulo': 'pt',
  'America/Manaus': 'pt',
  'America/Belem': 'pt',
  'America/Fortaleza': 'pt',
  'America/Recife': 'pt',
  'America/Bahia': 'pt',
  'America/Campo_Grande': 'pt',
  'America/Cuiaba': 'pt',
  'America/Porto_Velho': 'pt',
  'America/Boa_Vista': 'pt',
  'America/Rio_Branco': 'pt',
  'Africa/Luanda': 'pt',
  'Africa/Maputo': 'pt',
  'Africa/Bissau': 'pt',
  'Africa/Sao_Tome': 'pt',
  'Atlantic/Cape_Verde': 'pt',
  'Europe/Paris': 'fr',
  'Europe/Brussels': 'fr',
  'Europe/Luxembourg': 'fr',
  'Europe/Monaco': 'fr',
  'Indian/Reunion': 'fr',
  'Pacific/Noumea': 'fr',
  'America/Martinique': 'fr',
  'America/Guadeloupe': 'fr',
  'America/Cayenne': 'fr',
  'America/Miquelon': 'fr'
};

const getTimeZoneLanguage = (): SupportedLanguageCode | null => {
  if (typeof Intl === 'undefined') return null;

  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timeZoneLanguageMap[timeZone] ?? null;
  } catch {
    return null;
  }
};

const getBrowserLanguage = (): SupportedLanguageCode | null => {
  if (typeof navigator === 'undefined') return null;

  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const language of languages) {
    const normalized = normalizeLanguageCode(language);
    if (normalized) return normalized;
  }

  return null;
};

export const detectInitialLanguage = (): SupportedLanguageCode =>
  getStoredLanguage() ?? getTimeZoneLanguage() ?? getBrowserLanguage() ?? 'en';

export const persistLanguagePreference = (language: string) => {
  const normalized = normalizeLanguageCode(language);
  if (!normalized || typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(languagePreferenceKey, normalized);
  } catch {
    // Ignore storage failures such as private browsing restrictions.
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: detectInitialLanguage(),
    fallbackLng: 'en',
    supportedLngs: [...supportedLanguageCodes],
    load: 'languageOnly',
    interpolation: {
      escapeValue: false
    }
  });

const updateHtmlLang = (lng: string) => {
  if (typeof document === 'undefined') return;

  document.documentElement.lang = normalizeLanguageCode(lng) ?? 'en';
};

// 언어 변경 시 HTML lang 속성 업데이트
i18n.on('languageChanged', (lng) => {
  updateHtmlLang(lng);
});

updateHtmlLang(i18n.language);

export default i18n; 
